import { type FC, useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { IconExternalLink } from "@/assets/icons/geist/IconExternalLink";
import { Button } from "@/components/flexkit/Button";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { ERROR_ACTIONS, type ActionableError, type ErrorActionInfo } from "@/utils/environment-error-classifier";

type ActionableErrorProps = PropsWithClassName & {
    /** Classified error from the classifier or backend. */
    error: ActionableError;
    /** Override actions instead of looking them up from ERROR_ACTIONS. */
    actions?: ErrorActionInfo[];
    /** The original raw error message (shown in expandable section). */
    rawMessage?: string;
    "data-testid"?: string;
};

/**
 * Renders a classified error with a user-friendly message, action buttons,
 * and an expandable raw error section. Actions are resolved from ERROR_ACTIONS
 * by error code, or can be overridden via the `actions` prop.
 */
export const ActionableErrorMessage: FC<ActionableErrorProps> = ({
    error,
    actions: actionsProp,
    rawMessage,
    className,
    "data-testid": testId,
}) => {
    const [showRaw, setShowRaw] = useState(false);
    const actions = useMemo(() => actionsProp ?? ERROR_ACTIONS[error.errorCode] ?? [], [actionsProp, error.errorCode]);

    const repoUrl = error.metadata.repoUrl;

    return (
        <div className={cn("flex flex-col gap-2", className)} data-testid={testId}>
            <p className="text-sm text-content-primary">{error.userMessage}</p>

            {repoUrl && (
                <p className="truncate font-mono text-xs">
                    <a href={repoUrl} target="_blank" rel="noreferrer" className="text-content-link hover:underline">
                        {repoUrl}
                    </a>
                </p>
            )}

            {actions.length > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                    {actions.map((action, i) => (
                        <ActionButton key={action.actionId} action={action} isPrimary={i === 0} />
                    ))}
                </div>
            )}

            {rawMessage && (
                <div className="mt-1">
                    <button
                        type="button"
                        onClick={() => setShowRaw((prev) => !prev)}
                        data-tracking-id="actionable-error-toggle-details"
                        className="text-xs text-content-secondary transition-colors hover:text-content-primary"
                    >
                        <span>{showRaw ? "Hide details" : "Show details"}</span>
                        <span className="ml-1 font-mono text-content-tertiary">{error.errorCode}</span>
                    </button>
                    {showRaw && (
                        <pre className="mt-1 overflow-x-auto whitespace-pre-wrap break-words rounded bg-surface-02 p-2 text-xs text-content-secondary">
                            {rawMessage}
                        </pre>
                    )}
                </div>
            )}
        </div>
    );
};

const ActionButton: FC<{ action: ErrorActionInfo; isPrimary: boolean }> = ({ action, isPrimary }) => {
    const navigate = useNavigate();

    const handleClick = useCallback(() => {
        if (action.target.type === "dashboard_path") {
            navigate(action.target.path);
        } else {
            window.open(action.target.url, "_blank", "noopener,noreferrer");
        }
    }, [action.target, navigate]);

    const isExternal = action.target.type === "external_url";

    return (
        <Button
            variant={isPrimary ? "secondary" : "ghost"}
            size="sm"
            onClick={handleClick}
            data-tracking-id={`actionable-error-action-${action.actionId}`}
        >
            {action.label}
            {isExternal && <IconExternalLink size="sm" />}
        </Button>
    );
};
